import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useVideoService from '../../app/services/hooks/useVideoService';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import InputField from '../../components/InputField';
import { formatCLP } from '../Organization/types';
import { Video, formatDuration } from './types';

const FILTERS = [
    { value: '', label: 'Todos' },
    { value: 'free', label: 'Gratuitos' },
    { value: 'paid', label: 'De pago' },
    { value: 'mine', label: 'Mis compras' }
];

export const VideoCardItem: React.FC<{ video: Video; onOpen: () => void }> = ({ video, onOpen }) => (
    <div className="col-sm-6 col-lg-4 col-xl-3 mb-3">
        <button type="button" className="card h-100 w-100 text-start p-0 overflow-hidden" onClick={onOpen} data-testid={`video-card-${video.id}`}>
            <div className="bg-dark d-flex align-items-center justify-content-center position-relative" style={{ aspectRatio: '16 / 9', width: '100%' }}>
                {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <i className="fas fa-play-circle fa-3x text-white-50" />
                )}
                {video.duration_seconds ? (
                    <span className="badge bg-dark position-absolute" style={{ right: 8, bottom: 8 }}>{formatDuration(video.duration_seconds)}</span>
                ) : null}
            </div>
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                    <h6 className="mb-0">{video.title}</h6>
                    {video.is_free ? (
                        <span className="badge bg-success">Gratis</span>
                    ) : video.can_play ? (
                        <span className="badge bg-primary">{video.purchased ? 'Comprado' : 'Acceso'}</span>
                    ) : (
                        <span className="badge bg-warning text-dark">{formatCLP(video.price_with_tax)}</span>
                    )}
                </div>
                <small className="text-muted">{video.publisher}</small>
            </div>
        </button>
    </div>
);

const VideoCatalog: React.FC = () => {
    const history = useHistory();
    const { getCatalog, getMyPurchases, fetchingCatalog, fetchingPurchases } = useVideoService();

    const [videos, setVideos] = useState<Video[]>([]);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            if (filter === 'mine') {
                getMyPurchases({
                    onSuccess: (response) => setVideos(response.data.purchases.map((p: any) => p.video)),
                    onError: (response) => toast.error(response.message)
                });
                return;
            }
            getCatalog({ search, type: filter }, {
                onSuccess: (response) => setVideos(response.data.videos),
                onError: (response) => toast.error(response.message)
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [search, filter]);

    const loading = fetchingCatalog || fetchingPurchases;

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[{ label: 'Inicio', path: '/intranet' }, { label: 'Videos', isCurrent: true }]}
                title="Videos"
                description="Contenido legal de Monere. Los gratuitos se ven al tiro; los de pago se desbloquean con un pago único."
            />
            <DefaultCard>
                <div className="row align-items-end mb-3">
                    <div className="col-md-5 mb-2">
                        <InputField label="Buscar" value={search} onChange={setSearch} icon="fa-search" placeholder="Título o tema" />
                    </div>
                    <div className="col-md-7 mb-2">
                        <div className="btn-group flex-wrap" role="group">
                            {FILTERS.map((item) => (
                                <button key={item.value} type="button"
                                        className={`btn btn-sm ${filter === item.value ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setFilter(item.value)}>{item.label}</button>
                            ))}
                        </div>
                    </div>
                </div>
                {loading && videos.length === 0 ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
                ) : videos.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">
                        <i className="fas fa-film fa-3x d-block mb-3 text-secondary" />
                        {filter === 'mine' ? 'Aún no compras videos.' : 'No hay videos para mostrar.'}
                    </p>
                ) : (
                    <div className="row">
                        {videos.map((video) => (
                            <VideoCardItem key={video.id} video={video} onOpen={() => history.push(`/intranet/videos/${video.id}`)} />
                        ))}
                    </div>
                )}
            </DefaultCard>
        </div>
    );
};

export default VideoCatalog;
